/**
 * Presentational stage for one live avatar call: the avatar video, a live
 * transcript, the mic level meter, and the half-duplex / enable-audio controls.
 * Provider-neutral — driven entirely by a {@link UseAvatarCall} surface, so both
 * the sysadmin Avatar Lab and the tenant agent avatar call render the same stage
 * (the call lifecycle button lives with each consumer, since "end" means
 * different things — leave the lab page vs. close the dialog).
 *
 * The <video>/<audio> sinks are permanently mounted (never conditionally
 * rendered): the avatar track can subscribe before React commits the element, and
 * a conditional sink would miss it. The video is revealed — not mounted — once a
 * real frame decodes.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Video } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { BidiText, InfoTooltip } from '../common';
import { cn } from '../../../lib/utils';
import { MicLevelMeter } from './MicLevelMeter';
import type { UseAvatarCall } from './useAvatarCall';

interface AvatarCallStageProps {
  /** The call surface minus `start` — starting is each consumer's own flow. */
  call: Omit<UseAvatarCall, 'start'>;
  videoRef: React.RefObject<HTMLVideoElement>;
  audioRef: React.RefObject<HTMLAudioElement>;
  /** i18n key prefix under the `avatars` namespace (`lab` | `call`). */
  keyPrefix: string;
}

export function AvatarCallStage({ call, videoRef, audioRef, keyPrefix }: AvatarCallStageProps) {
  const { t } = useTranslation('avatars');
  const {
    status,
    transcript,
    videoReady,
    micLevel,
    micActive,
    micDeviceLabel,
    speakersMode,
    setSpeakersMode,
    micMuted,
    audioBlocked,
    enableAudio,
  } = call;
  const isLive = status === 'connecting' || status === 'connected';

  // Follow the transcript as it grows — keep the latest turn in view.
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript]);

  return (
    <div className="flex flex-col gap-4">
      {/* Avatar video */}
      <div className="relative mx-auto aspect-video max-h-[40vh] w-full overflow-hidden rounded-md bg-muted">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={cn(
            'h-full w-full object-contain transition-opacity',
            !videoReady && 'opacity-0',
          )}
        />
        <audio ref={audioRef} autoPlay className="hidden" />
        {!videoReady && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
            {isLive ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t(`${keyPrefix}.video.waiting`)}
              </>
            ) : (
              <>
                <Video className="h-5 w-5" />
                {t(`${keyPrefix}.video.placeholder`)}
              </>
            )}
          </div>
        )}
      </div>

      {/* Audio blocked → explicit enable control (autoplay policy) */}
      {audioBlocked && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2">
          <span className="text-sm text-warning-strong">{t(`${keyPrefix}.audio.blocked`)}</span>
          <Button size="sm" variant="outline" onClick={() => void enableAudio()}>
            {t(`${keyPrefix}.audio.enable`)}
          </Button>
        </div>
      )}

      {/* Mic meter + half-duplex toggle */}
      <div className="space-y-3 rounded-md border border-border p-3">
        <MicLevelMeter
          level={micLevel}
          active={micActive}
          deviceLabel={micDeviceLabel}
          keyPrefix={keyPrefix}
        />
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="avatar-call-speakers-mode" className="text-sm">
              {t(`${keyPrefix}.speakersMode.label`)}
            </Label>
            <InfoTooltip content={t(`${keyPrefix}.speakersMode.help`)} />
          </div>
          <Switch
            id="avatar-call-speakers-mode"
            checked={speakersMode}
            onCheckedChange={setSpeakersMode}
          />
        </div>
        {micMuted && (
          <p className="text-xs text-muted-foreground">{t(`${keyPrefix}.speakersMode.mutedNow`)}</p>
        )}
      </div>

      {/* Transcript */}
      <div className="flex flex-col">
        <h3 className="mb-1.5 text-sm font-semibold">{t(`${keyPrefix}.transcript.title`)}</h3>
        <div
          ref={transcriptScrollRef}
          className="max-h-48 min-h-24 space-y-3 overflow-y-auto rounded-md bg-muted/40 p-3"
        >
          {transcript.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t(`${keyPrefix}.transcript.empty`)}</p>
          ) : (
            transcript.map((line) => {
              const isUser = line.speaker === 'user';
              return (
                <div
                  key={line.id}
                  className={cn('flex flex-col gap-0.5', isUser ? 'items-end' : 'items-start')}
                >
                  <span className="text-xs font-medium text-muted-foreground">
                    {isUser ? t(`${keyPrefix}.transcript.you`) : t(`${keyPrefix}.transcript.avatar`)}
                  </span>
                  <p
                    className={cn(
                      'max-w-[85%] rounded-md px-2.5 py-1.5 text-sm',
                      isUser ? 'bg-module-accent/15' : 'bg-card',
                      line.final ? 'text-foreground' : 'italic text-muted-foreground',
                    )}
                  >
                    <BidiText>{line.text}</BidiText>
                  </p>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
